import {isHttpError} from '../../../util/requests';
import {type TileMediaErrorKind} from './inMemoryCache';

export type TileMediaErrorType =
  | 'MISSING_DATA' // No data available for the requested step
  | 'REQUEST_FAILED' // Network request failed (e.g. 50x errors)
  | 'TIMEOUT' // Request timed out
  | 'INVALID_DATA' // Data exists but is invalid/corrupted
  | 'UNAUTHORIZED' // Permission denied
  | 'EXPIRED_URL' // Signed URL rejected as expired; a fresh URL is expected to work
  | 'UNKNOWN'; // Other errors

/** Safe for logs/RUM; never includes signed-URL query params. */
export const GENERIC_MEDIA_BLOB_FAILURE_MESSAGE =
  'too many request failures for media fetch';

export class TileMediaError extends Error {
  constructor(
    message: string,
    public readonly type: TileMediaErrorType,
    public readonly originalError?: Error
  ) {
    super(message);
    this.name = 'TileMediaError';
  }
}

export function createTileMediaError(
  message: string,
  type: TileMediaErrorType,
  originalError?: Error
): TileMediaError {
  return new TileMediaError(message, type, originalError);
}

/** Provider-specific expiry detection lives in `requests.ts` (`HttpError.urlExpired`). */
export function isExpiredUrlError(err: unknown): boolean {
  if (err instanceof TileMediaError) {
    return err.type === 'EXPIRED_URL';
  }
  return isHttpError(err) && err.urlExpired;
}

/** `HTTP 403 SignatureDoesNotMatch`; safe to log, never contains the URL. */
export function describeHttpFailure(err: unknown): string | undefined {
  if (!isHttpError(err)) {
    return undefined;
  }
  return err.errorCode != null
    ? `HTTP ${err.status} ${err.errorCode}`
    : `HTTP ${err.status}`;
}

export function tileMediaErrorTypeFromHttpFailure(
  cause: unknown
): TileMediaErrorType {
  if (isExpiredUrlError(cause)) {
    return 'EXPIRED_URL';
  }
  if (cause instanceof Error && cause.message.startsWith('HTTP ')) {
    const status = Number.parseInt(cause.message.slice(5), 10);
    if (status === 403) {
      return 'UNAUTHORIZED';
    }
    if (!Number.isNaN(status)) {
      return 'REQUEST_FAILED';
    }
  }
  return 'UNKNOWN';
}

export function tileMediaErrorKindFromBlobFailure(
  err: unknown
): TileMediaErrorKind {
  if (err instanceof TileMediaError) {
    switch (err.type) {
      case 'UNAUTHORIZED':
        return 'media_blob_unauthorized';
      case 'EXPIRED_URL':
        return 'media_blob_url_expired';
      default:
        return 'media_blob_fetch_failed';
    }
  }
  if (isExpiredUrlError(err)) {
    return 'media_blob_url_expired';
  }
  if (err instanceof Error && err.message.startsWith('HTTP ')) {
    const status = Number.parseInt(err.message.slice(5), 10);
    if (status === 403) {
      return 'media_blob_unauthorized';
    }
    return 'media_blob_fetch_failed';
  }
  return 'media_blob_fetch_failed';
}

export function genericBlobFailureMessage(err: unknown): string {
  if (err instanceof TileMediaError) {
    return err.message;
  }
  if (err instanceof Error && err.message.startsWith('HTTP ')) {
    return GENERIC_MEDIA_BLOB_FAILURE_MESSAGE;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Failed to fetch media';
}
